import axios from "axios";
import API_BASE_URL from "./API_BASE_URL";
import { clearSession } from "../utils/authStorage";
import { csrfHeaders } from "../utils/csrf";

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true,
  timeout: 30000, // Increased timeout to 30 seconds
});

apiClient.interceptors.request.use(
  (config) => {
    // Task 149: the session travels in an HttpOnly cookie this code cannot
    // read; `withCredentials` above is what sends it. Nothing is attached here
    // any more.
    //
    // Purpose-limited requests (such as email verification) still set their
    // own Authorization header explicitly, and are untouched — that token is a
    // separate, short-lived credential, not the session.
    //
    // Task 150: the browser attaches the session cookie to cross-site requests
    // too, so state-changing calls echo the CSRF token. Another origin can
    // cause a request but cannot read our cookie to build this header.
    const method = String(config.method || "get").toUpperCase();
    if (!["GET", "HEAD", "OPTIONS"].includes(method)) {
      Object.assign(config.headers, csrfHeaders());
    }
    return config;
  },
  (error) => Promise.reject(error)
);

/**
 * Endpoints whose 401 is an answer, not an expired session. A wrong password
 * on login is a 401; refreshing and retrying it would be meaningless, and the
 * refresh endpoint must never trigger a refresh of itself.
 */
const NO_REFRESH_PATHS = [
  "/auth/refresh",
  "/auth/login",
  "/auth/logout",
  "/auth/signup",
  "/auth/verify-email",
  "/auth/resend-verification",
  "/auth/forgot-password",
  "/auth/reset-password",
];

function isNoRefreshPath(url?: string): boolean {
  const path = String(url || "").split("?")[0];
  return NO_REFRESH_PATHS.some((p) => path === p || path.endsWith(p) || path.includes(`${p}/`));
}

/**
 * A request that brings its own Authorization header (email verification,
 * for example) is using a separate, purpose-limited credential. A 401 there
 * says that token is bad; it says nothing about the session.
 */
function carriesOwnCredential(config: any): boolean {
  const h = config?.headers || {};
  return Boolean(h.Authorization || h.authorization);
}

let refreshInFlight: Promise<boolean> | null = null;

/**
 * Renews the session using the long-lived refresh cookie.
 *
 * The access cookie lives 15 minutes (task 149) and the browser deletes it
 * when it expires, so the next request simply arrives without one. Nothing
 * called /auth/refresh, which is why applicants were signed out after about
 * 15 minutes of use.
 *
 * Single-flight: a page that fires several requests at once gets several
 * 401s at once, and each must not start its own refresh — the second would
 * race the first's cookie rotation.
 *
 * Exported for the few raw fetch() callers (signature uploads) that do not
 * pass through this client.
 */
export function refreshSession(): Promise<boolean> {
  if (!refreshInFlight) {
    refreshInFlight = apiClient
      .post("/auth/refresh", undefined, { _skipRefresh: true } as any)
      .then(() => true)
      .catch(() => false)
      .finally(() => {
        refreshInFlight = null;
      });
  }
  return refreshInFlight;
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config: any = error.config || {};

    if (error.response?.status !== 401) {
      return Promise.reject(error);
    }

    const eligibleForRefresh =
      !config._skipRefresh &&
      !config._retried &&
      !isNoRefreshPath(config.url) &&
      !carriesOwnCredential(config);

    if (eligibleForRefresh && (await refreshSession())) {
      // Retry once. The request interceptor runs again, so the retry carries
      // the CSRF token the refresh just re-issued rather than the old one.
      return apiClient({ ...config, _retried: true });
    }

    // The session is genuinely over (or this 401 was never about it).
    //
    // The session cookie is HttpOnly and cannot be removed from here; only
    // the server can do that, and it has just told us the session is no
    // longer valid anyway.
    //
    // Everything identifying the user goes, not just the marker. Clearing
    // the marker alone left `userDetails` behind, and the header renders its
    // avatar and name from that — so the page still looked signed in while
    // every request was being refused. clearSession also notifies, so
    // components update immediately instead of at the next navigation.
    clearSession();
    return Promise.reject(error);
  }
);

/**
 * fetch() with the same session renewal as the axios client.
 *
 * Signature uploads use fetch directly (multipart, with the browser setting
 * the boundary), so they bypass the interceptor above. Without this they
 * would still fail after 15 idle minutes even though everything else renews.
 *
 * Takes a builder rather than a RequestInit so a retry is rebuilt from
 * scratch — the refresh re-issues the CSRF cookie, and a retry reusing the
 * old header would be refused.
 */
export async function fetchWithSessionRefresh(
  url: string,
  buildInit: () => RequestInit
): Promise<Response> {
  const first = await fetch(url, buildInit());
  if (first.status !== 401) return first;
  if (await refreshSession()) return fetch(url, buildInit());
  clearSession();
  return first;
}

export default apiClient;
